import type { HPOTerm } from '@/components/common/HPOSearch'
import { hpoAPIClient } from '@/features/terminology/hpo/HPOAPIClient'
import { resolveHpoLabel } from '@/features/terminology/hpo/hpoLabelResolver'
import { mapWithConcurrency } from '@/features/terminology/hpo/hpoLookupCache'
import type { HPONode } from '@/features/terminology/hpo/hpoTypes'
import { isHpoCode } from '@/features/terminology/hpo/hpoTypes'
import { mapTypeToCategory } from '@/utils/orderFormUtils'
import * as Sentry from '@sentry/react'
import { useCallback, useEffect, useRef } from 'react'

interface IndicationsState {
  phenotypes: HPOTerm[]
}

/** Stay under terminology search rate limits (burst 3 / ~10 per minute). */
const LOOKUP_CONCURRENCY = 2

/**
 * Resolve human-readable labels for indication phenotypes whose `name` is still
 * a raw HP code (legacy canonical rows). Resolution runs at most once per load;
 * call the returned `resetIndicationLabelResolution` after replacing the
 * phenotype set (e.g. when indications are re-derived) to allow it to run again.
 *
 * Shared by OrderStart and OrderInProgress; both previously inlined this effect.
 */
export function useResolveIndicationHpoLabels(
  indications: IndicationsState,
  setIndications: (next: IndicationsState) => void
): { resetIndicationLabelResolution: () => void } {
  const hasResolvedIndicationLabels = useRef(false)

  useEffect(() => {
    if (hasResolvedIndicationLabels.current) {
      return
    }
    if (indications.phenotypes.length === 0) {
      return
    }
    const needsResolution = indications.phenotypes.some((term) => isHpoCode(term.name))
    if (!needsResolution) {
      hasResolvedIndicationLabels.current = true
      return
    }

    let isActive = true
    const resolveLabels = async () => {
      try {
        const getTermById = (id: string) => hpoAPIClient.getHPOTermById(id)
        const updatedPhenotypes = await mapWithConcurrency(indications.phenotypes, LOOKUP_CONCURRENCY, async (term) => {
          if (!isHpoCode(term.name)) {
            return term
          }
          const resolved = await resolveHpoLabel(
            {
              id: term.id,
              label: term.name,
              category: mapTypeToCategory(term.type),
              node: {
                id: term.id,
                lbl: term.name,
                type: 'PhenotypicFeature',
              } as HPONode,
            },
            getTermById
          )
          return {
            ...term,
            id: resolved.id,
            hpoCode: resolved.id,
            name: resolved.label,
          }
        })
        if (!isActive) {
          return
        }
        setIndications({ phenotypes: updatedPhenotypes })
      } catch (error) {
        Sentry.captureException(error)
      } finally {
        // Always mark attempted so rate-limit failures cannot retry forever
        // when parent state keeps replacing the phenotypes array reference.
        hasResolvedIndicationLabels.current = true
      }
    }
    void resolveLabels()

    return () => {
      isActive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indications.phenotypes])

  const resetIndicationLabelResolution = useCallback(() => {
    hasResolvedIndicationLabels.current = false
  }, [])

  return { resetIndicationLabelResolution }
}
